import type { TFunction } from 'i18next';
import type { Arealplan, Dispensasjon, DispensasjonInput, DispensasjonType, DispensasjonFra } from '@origo/planregister';
import type { DispensationGroup, DispensationItem, DispensasjonFormValues } from '../types';
import { dispensasjonTypeLabel, dispensasjonFraLabel } from '../codelists/dispensasjonType';
import { formatDateShortNb } from './formatDate';
import { parseIsoDate } from './toProcessings';

function toDispensationItem(d: Dispensasjon, t: TFunction): DispensationItem {
    const beskrivelse = d.beskrivelse?.trim() || undefined;
    return {
        id: d.dispensasjonId,
        tittel: beskrivelse || dispensasjonTypeLabel(d.dispensasjonType, t),
        saksnummer: d.saksaar !== undefined && d.sakssekvensnummer !== undefined
            ? `${d.saksaar}/${String(d.sakssekvensnummer).padStart(3, '0')}`
            : '',
        dato: formatDateShortNb(d.vedtaksdato),
        dispensasjonTypeLabel: dispensasjonTypeLabel(d.dispensasjonType, t),
        dispensasjonFraLabel: dispensasjonFraLabel(d.dispensasjonFra, t),
        varighet: d.varigDispensasjon
            ? t('dispensations.detail.permanent')
            : [formatDateShortNb(d.varighetFra), formatDateShortNb(d.varighetTil)].filter(Boolean).join(' – '),
        begrunnelse: d.begrunnelse?.trim() || undefined,
        beskrivelse,
    };
}

export function toDispensationGroups(dispensasjoner: Dispensasjon[], t: TFunction): DispensationGroup[] {
    const approved: DispensationItem[] = [];
    const rejected: DispensationItem[] = [];
    for (const d of dispensasjoner) {
        const item = toDispensationItem(d, t);
        if (d.innvilgetDispensasjon) approved.push(item);
        else rejected.push(item);
    }
    return [
        { key: 'approved', count: approved.length, items: approved },
        { key: 'rejected', count: rejected.length, items: rejected },
    ];
}

export function emptyDispensasjonForm(): DispensasjonFormValues {
    return {
        dispensasjonType: '',
        dispensasjonFra: '',
        vedtaksdato: '',
        innvilgetDispensasjon: false,
        varigDispensasjon: true,
        varighetFra: '',
        varighetTil: '',
        saksnummer: '',
        begrunnelse: '',
        beskrivelse: '',
    };
}

const SAKSNUMMER_PATTERN = /^(\d{4})\/(\d+)$/;

/** Parser `"ÅÅÅÅ/NNN"` (jf. visningsformatet i `toDispensationItem`). Ugyldig
 *  eller tomt format gir `undefined` for begge feltene. */
function parseSaksnummer(value: string): { saksaar?: number; sakssekvensnummer?: number } {
    const match = SAKSNUMMER_PATTERN.exec(value.trim());
    if (!match) return {};
    return { saksaar: Number(match[1]), sakssekvensnummer: Number(match[2]) };
}

/** Sjekker om et saksnummer er tomt (gyldig, valgfritt felt) eller matcher `"ÅÅÅÅ/NNN"`. */
export function isValidSaksnummer(value: string): boolean {
    const trimmed = value.trim();
    return trimmed === '' || SAKSNUMMER_PATTERN.test(trimmed);
}

export function toDispensasjonInput(
    values: DispensasjonFormValues,
    napId: string,
    plan?: Arealplan | null
): DispensasjonInput {
    const { saksaar, sakssekvensnummer } = parseSaksnummer(values.saksnummer);
    return {
        dispensasjonId: crypto.randomUUID(),
        nasjonalArealplanId: napId,
        kommunenummer: plan?.admomrkode,
        planidentifikasjon: plan?.planId,
        dispensasjonType: (values.dispensasjonType || undefined) as DispensasjonType | undefined,
        dispensasjonFra: (values.dispensasjonFra || undefined) as DispensasjonFra | undefined,
        vedtaksdato: parseIsoDate(values.vedtaksdato)!,
        innvilgetDispensasjon: values.innvilgetDispensasjon,
        varigDispensasjon: values.varigDispensasjon,
        varighetFra: values.varigDispensasjon ? undefined : parseIsoDate(values.varighetFra),
        varighetTil: values.varigDispensasjon ? undefined : parseIsoDate(values.varighetTil),
        saksaar,
        sakssekvensnummer,
        begrunnelse: values.begrunnelse || undefined,
        beskrivelse: values.beskrivelse || undefined,
    };
}
